import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { stringProblems } from '../../data/practice/stringProblems';

const StringPractice: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>('all');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  // Get unique tags from all problems
  const allTags = useMemo(() => {
    const tags = new Set<string>();
    stringProblems.forEach(problem => {
      problem.tags.forEach(tag => tags.add(tag));
    });
    return Array.from(tags);
  }, []);

  // Filter problems based on search term, difficulty, and tags
  const filteredProblems = useMemo(() => {
    return stringProblems.filter(problem => {
      const matchesSearch = problem.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          problem.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesDifficulty = selectedDifficulty === 'all' || problem.difficulty === selectedDifficulty;
      const matchesTags = selectedTags.length === 0 || 
                         selectedTags.every(tag => problem.tags.includes(tag));
      
      return matchesSearch && matchesDifficulty && matchesTags;
    });
  }, [searchTerm, selectedDifficulty, selectedTags]);

  const handleTagToggle = (tag: string) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      {/* Minimal grid background */}
      <div className="fixed inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[length:64px_64px] [background-position-x:center]"></div>
      
      <div className="relative z-10 max-w-5xl mx-auto px-4 py-8">
        {/* Header with back button */}
        <div className="flex items-center justify-between mb-8">
          <motion.button
            onClick={() => navigate('/topics')}
            className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
            whileHover={{ x: -3 }}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Topics
          </motion.button>
        </div>

        {/* Title and description */}
        <div className="mb-12 text-center">
          <h1 className="text-4xl font-bold mb-4">String Practice</h1>
          <p className="text-white/60">Master string manipulation with these carefully curated problems.</p>
        </div>

        {/* Filters */}
        <div className="mb-8 space-y-4">
          {/* Search */}
          <input
            type="text"
            placeholder="Search problems..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-white/20 transition-colors"
          />

          {/* Difficulty filter */}
          <div className="flex gap-2">
            {['all', 'Easy', 'Medium', 'Hard'].map(difficulty => (
              <button
                key={difficulty}
                onClick={() => setSelectedDifficulty(difficulty)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  selectedDifficulty === difficulty
                    ? 'bg-white/20 text-white'
                    : 'bg-white/5 text-white/60 hover:bg-white/10'
                }`}
              >
                {difficulty === 'all' ? 'All Difficulties' : difficulty}
              </button>
            ))}
          </div>

          {/* Tags filter */}
          <div className="flex flex-wrap gap-2">
            {allTags.map(tag => (
              <button
                key={tag}
                onClick={() => handleTagToggle(tag)}
                className={`px-3 py-1 rounded-full text-sm transition-colors ${
                  selectedTags.includes(tag)
                    ? 'bg-white/20 text-white'
                    : 'bg-white/5 text-white/60 hover:bg-white/10'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>

        {/* Problems grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredProblems.map(problem => (
            <motion.div
              key={problem.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              whileHover={{ scale: 1.02 }}
              onClick={() => navigate(`/practice/strings/${problem.id}`)}
              className="bg-white/5 p-6 rounded-lg cursor-pointer hover:bg-white/10 transition-colors"
            >
              <h3 className="text-xl font-semibold mb-2">{problem.title}</h3>
              <div className="flex items-center gap-3 mb-4">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  problem.difficulty === 'Easy' ? 'bg-[#2cbb5d]/20 text-[#2cbb5d]' :
                  problem.difficulty === 'Medium' ? 'bg-[#ffa116]/20 text-[#ffa116]' :
                  'bg-[#ef4743]/20 text-[#ef4743]'
                }`}>
                  {problem.difficulty}
                </span>
                <div className="flex flex-wrap gap-2">
                  {problem.tags.map(tag => (
                    <span key={tag} className="text-sm text-white/50">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              <p className="text-white/60 text-sm line-clamp-2">{problem.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default StringPractice;
